import NoteMetaData from "../components/metaData";

/**
 * Object containing styles for various components.
 *
 * @typedef {Object} Styles
 * @property {Object} buttonStyleOrange - Styles for an orange button.
 * @property {string} buttonStyleOrange.color - The color of the button.
 * @property {string} buttonStyleOrange.fontSize - The font size of the button.
 * @property {string} buttonStyleOrange.fontWeight - The font weight of the button.
 * 
 * @property {Object} buttonStyleGreen - Styles for a green button.
 * @property {string} buttonStyleGreen.color - The color of the button.
 * @property {string} buttonStyleGreen.fontSize - The font size of the button.
 * @property {string} buttonStyleGreen.fontWeight - The font weight of the button.
 * 
 * @property {Object} buttonStyleBlue - Styles for a blue button.
 * @property {string} buttonStyleBlue.backgroundColor - The background color of the button.
 * @property {string} buttonStyleBlue.color - The color of the button.
 * @property {string} buttonStyleBlue.fontSize - The font size of the button.
 * @property {string} buttonStyleBlue.fontWeight - The font weight of the button.
 * @property {string} buttonStyleBlue.padding - The padding of the button.
 * @property {string} buttonStyleBlue.margin - The margin of the button.
 * 
 * @property {Object} buttonStyleDisabled - Styles for a disabled button.
 * @property {string} buttonStyleDisabled.backgroundColor - The background color of the button.
 * @property {string} buttonStyleDisabled.color - The color of the button.
 * @property {string} buttonStyleDisabled.fontSize - The font size of the button.
 * @property {string} buttonStyleDisabled.fontWeight - The font weight of the button.
 * @property {string} buttonStyleDisabled.opacity - The opacity of the button.
 * @property {string} buttonStyleDisabled.margin - The margin of the button.
 * 
 * @property {Object} symbolStyle - Styles for a symbol.
 * @property {string} symbolStyle.color - The color of the symbol.
 * @property {string} symbolStyle.minWidth - The minimum width of the symbol.
 * @property {Object} symbolStyle.& .MuiFormLabel-root - Styles for the root element of the MuiFormLabel component.
 * @property {string} symbolStyle.& .MuiFormLabel-root.color - The color of the MuiFormLabel component.
 * @property {string} symbolStyle.& .MuiFormLabel-root.borderBottomColor - The border bottom color of the MuiFormLabel component.
 * @property {Object} symbolStyle.& .MuiFormLabel-root.& ::after - Styles for the ::after pseudo-element of the MuiFormLabel component.
 * @property {string} symbolStyle.& .MuiFormLabel-root.& ::after.borderColor - The border color of the ::after pseudo-element.
 * @property {Object} symbolStyle.& .MuiInputBase-input - Styles for the input element of the MuiInputBase component.
 * @property {string} symbolStyle.& .MuiInputBase-input.borderColor - The border color of the MuiInputBase component.
 * @property {Object} symbolStyle.& .MuiInputBase-input.& ::after - Styles for the ::after pseudo-element of the MuiInputBase component.
 * @property {string} symbolStyle.& .MuiInputBase-input.& ::after.borderColor - The border color of the ::after pseudo-element.
 * @property {Object} symbolStyle.& .MuiSelect-select - Styles for the select element of the MuiSelect component.
 * @property {string} symbolStyle.& .MuiSelect-select.color - The color of the MuiSelect component.
 * @property {string} symbolStyle.& .MuiSelect-select.borderBottomColor - The border bottom color of the MuiSelect component.
 * @property {Object} symbolStyle.& .MuiSvgIcon-root - Styles for the root element of the MuiSvgIcon component.
 * @property {string} symbolStyle.& .MuiSvgIcon-root.color - The color of the MuiSvgIcon component.
 * @property {string} symbolStyle.& .MuiSvgIcon-root.borderBottomColor - The border bottom color of the MuiSvgIcon component.
 * @property {Object} symbolStyle.& .MuiInputBase-root:after - Styles for the :after pseudo-element of the MuiInputBase component.
 * @property {string} symbolStyle.& .MuiInputBase-root:after.color - The color of the :after pseudo-element.
 * @property {string} symbolStyle.& .MuiInputBase-root:after.borderBottomColor - The border bottom color of the :after pseudo-element.
 * @property {Object} symbolStyle.& .MuiInputBase-root:before - Styles for the :before pseudo-element of the MuiInputBase component.
 * @property {string} symbolStyle.& .MuiInputBase-root:before.color - The color of the :before pseudo-element.
 * @property {string} symbolStyle.& .MuiInputBase-root:before.borderBottomColor - The border bottom color of the :before pseudo-element.
 * 
 * @property {Object} diagramCardStyle - Styles for a diagram card.
 * @property {string} diagramCardStyle.margin - The margin of the diagram card.
 * @property {string} diagramCardStyle.borderColor - The border color of the diagram card.
 * @property {string} diagramCardStyle.borderWidth - The border width of the diagram card.
 * @property {string} diagramCardStyle.borderStyle - The border style of the diagram card.
 * @property {string} diagramCardStyle.borderRadius - The border radius of the diagram card.
 * @property {Object} diagramCardStyle.&:hover - Styles for the diagram card when hovered.
 * @property {string} diagramCardStyle.&:hover.backgroundColor - The background color of the diagram card when hovered.
 * @property {string} diagramCardStyle.&:hover.color - The color of the diagram card when hovered.
 * 
 * @property {Object} diagramCardContentStyle - Styles for the content of a diagram card.
 * @property {string} diagramCardContentStyle.padding - The padding of the diagram card content.
 * @property {string} diagramCardContentStyle.textAlign - The text alignment of the diagram card content.
 * @property {string} diagramCardContentStyle.display - The display property of the diagram card content.
 * @property {string} diagramCardContentStyle.flexDirection - The flex direction of the diagram card content.
 * @property {string} diagramCardContentStyle.alignItems - The align items property of the diagram card content.
 * 
 * @property {Object} displayButtonStyle - Styles for a display button.
 * @property {string} displayButtonStyle.fontSize - The font size of the display button.
 * @property {string} displayButtonStyle.padding - The padding of the display button.
 * @property {string} displayButtonStyle.margin - The margin of the display button.
 * @property {string} displayButtonStyle.borderRadius - The border radius of the display button.
 * @property {string} displayButtonStyle.color - The color of the display button.
 * @property {string} displayButtonStyle.border - The border of the display button.
 * @property {string} displayButtonStyle.cursor - The cursor of the display button.
 * @property {string} displayButtonStyle.transition - The transition property of the display button.
 * @property {Object} displayButtonStyle.&:hover - Styles for the display button when hovered.
 * @property {string} displayButtonStyle.&:hover.backgroundColor - The background color of the display button when hovered.
 * @property {string} displayButtonStyle.&:hover.color - The color of the display button when hovered.
 * @property {Object} displayButtonStyle.&:disabled - Styles for the display button when disabled.
 * @property {string} displayButtonStyle.&:disabled.cursor - The cursor of the display button when disabled.
 * @property {string} displayButtonStyle.&:disabled.color - The color of the display button when disabled.
 * @property {string} displayButtonStyle.&:disabled.opacity - The opacity of the display button when disabled.
 * 
 * @property {Object} boxStyle - Styles for a box.
 * @property {string} boxStyle.display - The display property of the box.
 * @property {string} boxStyle.flexDirection - The flex direction of the box.
 * 
 * @property {Object} headerStyle - Styles for a header.
 * @property {string} headerStyle.display - The display property of the header.
 * @property {string} headerStyle.justifyContent - The justify content property of the header.
 * @property {string} headerStyle.alignItems - The align items property of the header.
 * @property {string} headerStyle.marginBottom - The margin bottom of the header.
 * @property {string} headerStyle.flexDirection - The flex direction of the header.
 * 
 * @property {Object} fretboardDisplayBox - Styles for a fretboard display box.
 * @property {string} fretboardDisplayBox.display - The display property of the fretboard display box.
 * @property {string} fretboardDisplayBox.flexWrap - The flex wrap property of the fretboard display box.
 * @property {string} fretboardDisplayBox.justifyContent - The justify content property of the fretboard display box.
 * @property {string} fretboardDisplayBox.backgroundColor - The background color of the fretboard display box.
 * @property {string} fretboardDisplayBox.borderColor - The border color of the fretboard display box.
 * @property {string} fretboardDisplayBox.borderWidth - The border width of the fretboard display box.
 * @property {string} fretboardDisplayBox.borderStyle - The border style of the fretboard display box.
 * @property {string} fretboardDisplayBox.borderRadius - The border radius of the fretboard display box.
 * @property {string} fretboardDisplayBox.padding - The padding of the fretboard display box.
 * 
 * @property {Object} fretboardDescriptionBox - Styles for a fretboard description box.
 * @property {string} fretboardDescriptionBox.color - The color of the fretboard description box.
 * @property {string} fretboardDescriptionBox.fontSize - The font size of the fretboard description box.
 * @property {string} fretboardDescriptionBox.width - The width of the fretboard description box.
 * 
 * @property {Object} stackStyle - Styles for a stack.
 * @property {string} stackStyle.display - The display property of the stack.
 * @property {string} stackStyle.flexDirection - The flex direction of the stack.
 * @property {string} stackStyle.alignItems - The align items property of the stack.
 * @property {string} stackStyle.justifyContent - The justify content property of the stack.
 * @property {string} stackStyle.paddingY - The padding on the y-axis of the stack.
 * 
 * @property {Object} fretboardHeadingStyle - Styles for a fretboard heading.
 * @property {string} fretboardHeadingStyle.color - The color of the fretboard heading.
 * @property {string} fretboardHeadingStyle.fontSize - The font size of the fretboard heading.
 * @property {string} fretboardHeadingStyle.fontWeight - The font weight of the fretboard heading.
 * @property {string} fretboardHeadingStyle.margin - The margin of the fretboard heading.
 * @property {string} fretboardHeadingStyle.textAlign - The text alignment of the fretboard heading.
 */
const styles = {
    buttonStyleOrange: {
        color: "#e5893e",
        fontSize: "1.2rem",
        fontWeight: "bold",
    },
    buttonStyleGreen: {
        color: "#61897d",
        fontSize: "1.2rem",
        fontWeight: "bold",
    },
    buttonStyleBlue: {
        backgroundColor: "#1a578e",
        color: "#fff",
        fontSize: "1.2rem",
        fontWeight: "bold",
        padding: "5px 15px",
        margin: ".05em",
    },
    buttonStyleDisabled: {
        backgroundColor: "#F5F5F5",
        color: "#e5893e",
        fontSize: "1.2rem",
        fontWeight: "bold",
        opacity: "0.5",
        margin: ".05em",
    },
    symbolStyle: {
        color: "##1a578e",
        minWidth: "150px",
        '& .MuiFormLabel-root': {
            color: "#1a578e !important",
            borderBottomColor: "#1a578e !important",
            '& ::after': {
                borderColor: "#e5893e !important"
            },
        },
        '& .MuiInputBase-input': {
            borderColor: "#1a578e !important",
            '& ::after': {
                borderColor: "#1a578e !important"
            },
        },
        '& .MuiSelect-select': {
            color: "#1a578e",
            borderBottomColor: "#1a578e !important"
        },
        '& .MuiSvgIcon-root': {
            color: "#e51a578e893e !important",
            borderBottomColor: "#1a578e !important"
        },
        '& .MuiInputBase-root:after': {
            color: "#1a578e !important",
            borderBottomColor: "#1a578e !important"
        },
        '& .MuiInputBase-root:before': {
            color: "#1a578e !important",
            borderBottomColor: "#1a578e !important"
        },
    },
    diagramCardStyle: {

        margin: "16px",
        borderColor: "#1a578e",
        borderWidth: "2px",
        borderStyle: "solid",
        borderRadius: "8px",
        "&:hover": {
            backgroundColor: "#1a578e",
            color: "#1e262d",
        },
    },
    diagramCardContentStyle: {
        padding: "0.5rem",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    displayButtonStyle: {
        fontSize: "0.8rem",
        padding: "0.5rem 1rem",
        margin: "0.5rem",
        borderRadius: "0.25rem",
        color: "#e5893e",
        border: "none",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
            backgroundColor: "#e5893e",
            color: "#1e262d",
        },
        "&:disabled": {
            cursor: "not-allowed",
            color: "#e5893e !important",
            opacity: "0.5",
        },
    },
    boxStyle: {
        display: 'flex',
        flexDirection: 'column',
    },
    headerStyle: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: { xs: 'inherit', sm: 'center' },
        marginBottom: '1rem',
        flexDirection: { xs: "column", sm: "row", lg: "row" },
    },
    fretboardDisplayBox: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        backgroundColor: "#f5f5f5 ",
        borderColor: "#1a578e",
        borderWidth: "2px",
        borderStyle: "solid",
        borderRadius: "8px",
        padding: "16px",
    },
    fretboardDescriptionBox: {
        color: "#1a578e",
        fontSize: "1rem",
        width: "100vw",
    },
    stackStyle: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingY: '5px'
    },
    fretboardHeadingStyle: {
        color: "#1a578e",
        fontSize: "1.5rem",
        fontWeight: "bold",
        margin: "0 auto",
        textAlign: "center",
    },
    NoteMetaDataLabel: {
        color: "#1a578e",
        fontSize: "1rem",
        fontWeight: "bold",
        margin: "0 auto",
        textAlign: "center",
    }
};

export default styles;

